import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  XMarkIcon,
  ArrowPathIcon,
  CubeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

const EVENT_OPTIONS = [
  'qrcode',
  'history',
  'connection',
  'messages',
  'messages_update',
  'newsletter_messages',
  'call',
  'contacts',
  'presence',
  'groups',
  'labels',
  'chats',
  'chat_labels',
  'blocks',
  'sender'
];

const EXCLUDE_OPTIONS = [
  'wasSentByApi',
  'wasNotSentByApi',
  'fromMeYes',
  'fromMeNo',
  'isGroupYes',
  'isGroupNo'
];

const EMPTY_DRAFT = {
  id: null,
  enabled: true,
  url: '',
  events: ['messages', 'connection'],
  excludeMessages: ['wasSentByApi'],
  addUrlEvents: false,
  addUrlTypesMessages: false
};

function buildAuth() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function webhookGetUrl(apiBaseUrl, instance) {
  if (!apiBaseUrl || !instance) return null;
  if (instance._id) return `${apiBaseUrl}/api/whatsapp-instances/${instance._id}/webhook`;
  if (instance.uazapiToken) {
    return `${apiBaseUrl}/api/whatsapp-instances/uazapi-token/webhook?token=${encodeURIComponent(instance.uazapiToken)}`;
  }
  return null;
}

function webhookPostUrl(apiBaseUrl, instance) {
  if (!apiBaseUrl || !instance) return null;
  if (instance._id) return `${apiBaseUrl}/api/whatsapp-instances/${instance._id}/webhook`;
  if (instance.uazapiToken) return `${apiBaseUrl}/api/whatsapp-instances/uazapi-token/webhook`;
  return null;
}

function webhookToDraft(wh) {
  if (!wh) return { ...EMPTY_DRAFT };
  return {
    id: wh.id || null,
    enabled: wh.enabled !== false,
    url: wh.url || '',
    events: Array.isArray(wh.events) && wh.events.length ? [...wh.events] : ['messages'],
    excludeMessages:
      Array.isArray(wh.excludeMessages) && wh.excludeMessages.length
        ? [...wh.excludeMessages]
        : ['wasSentByApi'],
    addUrlEvents: Boolean(wh.addUrlEvents),
    addUrlTypesMessages: Boolean(wh.addUrlTypesMessages)
  };
}

function buildFinalUrlPreview(url, draft) {
  const base = String(url || '').trim().replace(/\/$/, '');
  if (!base) return '';
  let out = base;
  if (draft.addUrlEvents && draft.events?.length) {
    out += `/${draft.events[0]}`;
    if (draft.addUrlTypesMessages) out += '/{tipo_mensagem}';
  } else if (draft.addUrlTypesMessages) {
    out += '/{tipo_mensagem}';
  }
  return out;
}

function EmptyWebhookIllustration() {
  return (
    <div className="mx-auto flex h-28 w-28 flex-col items-center justify-center">
      <div className="relative">
        <div className="h-16 w-20 rounded-md border-2 border-amber-700/40 bg-amber-600/20" />
        <div className="absolute -top-1 left-1/2 h-3 w-14 -translate-x-1/2 rounded-sm border-2 border-amber-700/40 bg-amber-600/30" />
        <CubeIcon className="absolute bottom-2 left-1/2 h-8 w-8 -translate-x-1/2 text-amber-700/50" />
      </div>
    </div>
  );
}

function TagMultiSelect({ label, hint, options, value, onChange, placeholder = 'Clique para selecionar…' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const remove = (id, e) => {
    e?.stopPropagation();
    onChange(value.filter((x) => x !== id));
  };

  const toggleOption = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div ref={rootRef} className="relative space-y-2">
      <Label>{label}</Label>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'min-h-[44px] cursor-pointer rounded-md border border-input bg-background/80 px-2 py-2 transition',
          open && 'ring-2 ring-primary/40'
        )}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {value.length ? (
            value.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground"
              >
                <button
                  type="button"
                  className="mr-1 leading-none opacity-80 hover:opacity-100"
                  aria-label={`Remover ${tag}`}
                  onClick={(e) => remove(tag, e)}
                >
                  ×
                </button>
                {tag}
              </span>
            ))
          ) : (
            <span className="px-1 text-sm text-muted-foreground">{placeholder}</span>
          )}
        </div>
      </div>
      {open ? (
        <div
          className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto rounded-md border border-border bg-card py-1 shadow-lg"
          role="listbox"
        >
          {options.map((opt) => {
            const selected = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={selected}
                className={cn(
                  'block w-full px-3 py-2 text-left text-sm transition',
                  selected
                    ? 'bg-primary/25 font-medium text-primary'
                    : 'text-foreground hover:bg-muted/60'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOption(opt);
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function WebhookEditor({
  draft,
  setDraft,
  index,
  total,
  isNew,
  saving,
  onPrev,
  onNext,
  onSave,
  onSaveAsNew,
  onCancelNew,
  onDelete
}) {
  const finalUrl = useMemo(() => buildFinalUrlPreview(draft.url, draft), [draft]);

  const validate = () => {
    if (!String(draft.url || '').trim()) {
      toast.error('Informe a URL do webhook.');
      return false;
    }
    if (!draft.events?.length) {
      toast.error('Selecione ao menos um evento.');
      return false;
    }
    return true;
  };

  return (
    <div className="space-y-5">
      {total > 0 ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index <= 0}
            onClick={onPrev}
            aria-label="Webhook anterior"
          >
            <ChevronLeftIcon className="h-5 w-5 text-primary" />
          </Button>
          <span className="min-w-[4rem] text-center text-sm font-medium text-primary">
            {isNew ? 'Novo' : `${index + 1} / ${total}`}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index >= total - 1}
            onClick={onNext}
            aria-label="Próximo webhook"
          >
            <ChevronRightIcon className="h-5 w-5 text-primary" />
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label htmlFor="wh-enabled" className="flex cursor-pointer items-center gap-2">
          <span>Habilitado</span>
          <button
            id="wh-enabled"
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            onClick={() => setDraft((d) => ({ ...d, enabled: !d.enabled }))}
            className={cn(
              'relative h-6 w-11 rounded-full transition',
              draft.enabled ? 'bg-emerald-500' : 'bg-muted'
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition',
                draft.enabled ? 'left-[22px]' : 'left-0.5'
              )}
            />
          </button>
        </Label>
        {draft.id ? (
          <p className="font-mono text-xs text-muted-foreground">
            id: <span className="text-foreground">{draft.id}</span>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Novo webhook (sem ID ainda)</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-600 hover:bg-emerald-600">POST</Badge>
          <Label htmlFor="wh-url">URL</Label>
        </div>
        <Input
          id="wh-url"
          type="url"
          placeholder="https://ia.discador.net"
          value={draft.url}
          onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
          <span>addUrlEvents</span>
          <input
            type="checkbox"
            checked={draft.addUrlEvents}
            onChange={(e) => setDraft((d) => ({ ...d, addUrlEvents: e.target.checked }))}
            className="rounded border-input"
          />
        </label>
        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
          <span>addUrlTypesMessages</span>
          <input
            type="checkbox"
            checked={draft.addUrlTypesMessages}
            onChange={(e) => setDraft((d) => ({ ...d, addUrlTypesMessages: e.target.checked }))}
            className="rounded border-input"
          />
        </label>
      </div>

      <div className="space-y-1">
        <Label className="text-muted-foreground">URL final</Label>
        <p className="break-all rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-xs text-foreground">
          {finalUrl || '—'}
        </p>
      </div>

      <TagMultiSelect
        label="Escutar eventos"
        hint='Inclua "messages" para novas mensagens.'
        options={EVENT_OPTIONS}
        value={draft.events}
        onChange={(events) => setDraft((d) => ({ ...d, events }))}
        placeholder="Selecione os eventos…"
      />

      <TagMultiSelect
        label="Excluir dos eventos escutados"
        hint="wasSentByApi evita loops; isGroupYes ignora grupos."
        options={EXCLUDE_OPTIONS}
        value={draft.excludeMessages}
        onChange={(excludeMessages) => setDraft((d) => ({ ...d, excludeMessages }))}
        placeholder="Selecione os filtros…"
      />

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {!isNew && draft.id ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={saving}
            onClick={() => {
              if (window.confirm('Remover este webhook na UAZAPI?')) onDelete();
            }}
            aria-label="Excluir webhook"
          >
            <TrashIcon className="h-5 w-5" />
          </Button>
        ) : (
          <span className="w-10" />
        )}
        {isNew ? (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={saving}
            onClick={onCancelNew}
          >
            Cancelar
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={saving}
            onClick={() => {
              if (!validate()) return;
              onSaveAsNew();
            }}
          >
            Salvar com um novo
          </Button>
        )}
        <Button
          type="button"
          className="flex-1"
          disabled={saving}
          onClick={() => {
            if (!validate()) return;
            onSave();
          }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}

export default function WhatsAppWebhookModal({ open, instance, apiBaseUrl, onClose }) {
  const [view, setView] = useState('loading');
  const [webhooks, setWebhooks] = useState([]);
  const [editorIndex, setEditorIndex] = useState(0);
  const [isNewDraft, setIsNewDraft] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const postWebhook = useCallback(
    async (payload) => {
      const postUrl = webhookPostUrl(apiBaseUrl, instance);
      if (!postUrl) throw new Error('Instância sem identificador.');
      const body =
        !instance._id && instance.uazapiToken ? { ...payload, token: instance.uazapiToken } : payload;
      const res = await axios.post(postUrl, body, buildAuth());
      const list = res.data?.webhooks ?? [];
      setWebhooks(list);
      return list;
    },
    [apiBaseUrl, instance]
  );

  const loadWebhooks = useCallback(async () => {
    const getUrl = webhookGetUrl(apiBaseUrl, instance);
    if (!getUrl) {
      setError('Instância sem identificador.');
      setView('empty');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(getUrl, buildAuth());
      const list = res.data?.webhooks ?? [];
      setWebhooks(list);
      if (list.length) {
        setEditorIndex(0);
        setIsNewDraft(false);
        setDraft(webhookToDraft(list[0]));
        setView('editor');
      } else {
        setView('empty');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Falha ao consultar webhook.');
      setView('empty');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, instance]);

  useEffect(() => {
    if (!open) {
      setView('loading');
      setWebhooks([]);
      setEditorIndex(0);
      setIsNewDraft(false);
      setDraft({ ...EMPTY_DRAFT });
      setError(null);
      return;
    }
    void loadWebhooks();
  }, [open, loadWebhooks]);

  const goToIndex = (idx) => {
    if (!webhooks.length) return;
    const safe = Math.max(0, Math.min(idx, webhooks.length - 1));
    setEditorIndex(safe);
    setIsNewDraft(false);
    setDraft(webhookToDraft(webhooks[safe]));
  };

  const startNewDraft = () => {
    setIsNewDraft(true);
    setDraft({ ...EMPTY_DRAFT });
    setView('editor');
  };

  const draftToPayload = (d, { action } = {}) => {
    const base = {
      enabled: d.enabled,
      url: String(d.url).trim(),
      events: d.events,
      excludeMessages: d.excludeMessages,
      addUrlEvents: d.addUrlEvents,
      addUrlTypesMessages: d.addUrlTypesMessages
    };
    if (action === 'delete') return { action: 'delete', id: d.id };
    if (action === 'add') return { action: 'add', ...base };
    if (action === 'update') return { action: 'update', id: d.id, ...base };
    return base;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload;
      if (draft.id && !isNewDraft) {
        payload = draftToPayload(draft, { action: 'update' });
      } else if (webhooks.length === 0) {
        payload = draftToPayload(draft);
      } else {
        payload = draftToPayload(draft, { action: 'add' });
      }
      const list = await postWebhook(payload);
      toast.success('Webhook salvo.');
      const savedId = draft.id;
      setIsNewDraft(false);
      setView('editor');
      if (!list.length) {
        setView('empty');
        return;
      }
      setWebhooks(list);
      const idx = savedId ? list.findIndex((w) => w.id === savedId) : list.length - 1;
      goToIndex(idx >= 0 ? idx : list.length - 1);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsNew = async () => {
    setSaving(true);
    try {
      const payload =
        webhooks.length === 0 ? draftToPayload(draft) : draftToPayload(draft, { action: 'add' });
      const list = await postWebhook(payload);
      toast.success('Novo webhook criado.');
      setIsNewDraft(false);
      setView('editor');
      if (list.length) goToIndex(list.length - 1);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao criar webhook.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft.id) return;
    setSaving(true);
    try {
      const list = await postWebhook(draftToPayload(draft, { action: 'delete' }));
      toast.success('Webhook removido.');
      if (list.length) {
        goToIndex(Math.min(editorIndex, list.length - 1));
        setWebhooks(list);
      } else {
        setView('empty');
        setWebhooks([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Falha ao excluir.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const instanceLabel =
    instance?.name || instance?.uazapiInstanceName || instance?.uazapiInstanceId || 'Instância';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wh-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-xl border border-border bg-card text-card-foreground shadow-2xl">
        <button
          type="button"
          className="absolute right-3 top-3 z-10 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => onClose?.()}
          aria-label="Fechar"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="shrink-0 border-b border-border px-6 py-4 pr-12">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 id="wh-modal-title" className="text-lg font-semibold text-foreground">
                Webhooks
              </h2>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{instanceLabel}</p>
            </div>
            {view === 'editor' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={loading}
                onClick={() => void loadWebhooks()}
              >
                <ArrowPathIcon className={cn('h-4 w-4', loading && 'animate-spin')} />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {view === 'loading' || loading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Carregando…</p>
          ) : view === 'empty' ? (
            <div className="flex flex-col items-center py-4 text-center">
              <EmptyWebhookIllustration />
              <p className="mt-6 text-base font-medium text-foreground">Nenhum webhook encontrado</p>
              {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
              <Button type="button" className="mt-8 w-full max-w-xs" onClick={startNewDraft}>
                Criar Webhook
              </Button>
            </div>
          ) : (
            <WebhookEditor
              draft={draft}
              setDraft={setDraft}
              index={editorIndex}
              total={webhooks.length}
              isNew={isNewDraft}
              saving={saving}
              onPrev={() => goToIndex(editorIndex - 1)}
              onNext={() => goToIndex(editorIndex + 1)}
              onSave={handleSave}
              onSaveAsNew={handleSaveAsNew}
              onCancelNew={() => {
                setIsNewDraft(false);
                if (webhooks.length) goToIndex(Math.min(editorIndex, webhooks.length - 1));
                else setView('empty');
              }}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </div>
  );
}
